import type { MathFieldElement } from './MathLiveField';
import { EMPTY_MATH_SLOT_FILL } from './answerSlotAuthoring';
import { mathSlotIds } from '../../utils/evaluation/math/mathAnswerSlot';

// The student answer slots of a slot expression (PBI 538086), as the <math-field> sees them: MathLive
// prompts, the only editable parts of a read-only field. Kept apart from MathLiveField because every
// operation here is about which slot holds what, not about the field's own lifecycle.

export type SlotValues = Record<string, string>;
// true → correct, false → incorrect, absent → not graded yet.
export type SlotStates = Record<string, boolean | undefined>;

const SILENT = { silenceNotifications: true };

/** Whether a slot holds nothing but the invisible digit an empty slot shows (`EMPTY_MATH_SLOT_FILL`). */
const isEmptySlot = (element: MathFieldElement, id: string) =>
  (element.getPromptValue?.(id, 'latex') ?? '').replace(EMPTY_MATH_SLOT_FILL, '').trim() === '';

/** Show `template` read-only, with each slot holding its value. */
export function seedTemplate(element: MathFieldElement, template: string, values: SlotValues | undefined) {
  element.readOnly = true;
  element.setValue?.(template);
  writeSlots(element, values);
}

/**
 * Keep every slot's on-screen filler right as the student types: an empty slot holds the invisible digit
 * (`EMPTY_MATH_SLOT_FILL`) that gives it a filled slot's size, and it leaves the slot as soon as there is a
 * value. Works from what each slot holds NOW — never from the stored values, which trail the keystrokes —
 * so fast typing is never overwritten.
 */
export function fitEmptySlots(element: MathFieldElement, toStored: (latex: string) => string) {
  for (const id of element.getPrompts?.() ?? []) {
    const raw = element.getPromptValue?.(id, 'latex') ?? '';
    const value = toStored(raw);
    const hasFill = raw.includes(EMPTY_MATH_SLOT_FILL);
    if (value && hasFill) element.setPromptValue?.(id, value, SILENT);
    else if (!value && !hasFill) element.setPromptValue?.(id, EMPTY_MATH_SLOT_FILL, SILENT);
  }
}

/**
 * Take the invisible digit out of the slot the caret is in, ahead of a keystroke that inserts into it.
 * Removed after the keystroke instead (by `fitEmptySlots`), it would share the slot with the new
 * character for a frame: the slot drawn two digits wide and the formula around it shifting. Cleared
 * here, MathLive inserts into an empty slot, and both changes land before the browser paints.
 */
export function clearFillAtCaret(element: MathFieldElement) {
  const id = slotAtCaret(element);
  if (!id || !isEmptySlot(element, id)) return;
  if (!(element.getPromptValue?.(id, 'latex') ?? '').includes(EMPTY_MATH_SLOT_FILL)) return;
  element.setPromptValue?.(id, '', SILENT);
  const range = element.getPromptRange?.(id);
  if (range) element.position = range[1];
}

// A delete's input type, and the command that performs the same delete.
const DELETE_COMMANDS: Record<string, string> = {
  deleteContentBackward: 'deleteBackward',
  deleteContentForward: 'deleteForward',
  deleteWordBackward: 'deletePreviousWord',
  deleteWordForward: 'deleteNextWord'
};

// Fields performing a delete of their own, whose `beforeinput` must pass through.
const deleting = new WeakSet<MathFieldElement>();

/**
 * Perform a delete in place of MathLive's and give an emptied slot its invisible digit back at once.
 * Left to MathLive, the emptied slot is drawn at an empty slot's smaller size for a frame before
 * `fitEmptySlots` restores the digit, and the formula around it jumps. Returns whether it handled
 * the event (a delete); the caller then reads the slots at once.
 */
export function deleteKeepingSlotSize(
  element: MathFieldElement,
  event: InputEvent,
  toStored: (latex: string) => string
): boolean {
  const command = DELETE_COMMANDS[event.inputType];
  if (!command || deleting.has(element)) return false;
  event.preventDefault();
  deleting.add(element);
  try {
    element.executeCommand?.(command);
  } finally {
    deleting.delete(element);
  }
  fitEmptySlots(element, toStored);
  return true;
}

export function hasAllSlots(element: MathFieldElement, template: string) {
  const prompts = element.getPrompts?.() ?? [];
  return mathSlotIds(template).every(id => prompts.includes(id));
}

export function readSlots(element: MathFieldElement, toStored: (latex: string) => string): SlotValues {
  return (element.getPrompts?.() ?? []).reduce<SlotValues>((values, id) => {
    values[id] = toStored(element.getPromptValue?.(id, 'latex') ?? '');
    return values;
  }, {});
}

/** Write only the slots whose value differs, so a slot being typed into is never overwritten. */
export function writeSlots(
  element: MathFieldElement,
  values: SlotValues | undefined,
  toStored: (latex: string) => string = latex => latex
) {
  for (const id of element.getPrompts?.() ?? []) {
    const next = values?.[id] ?? '';
    const raw = element.getPromptValue?.(id, 'latex') ?? '';
    if (toStored(raw) !== toStored(next) || (!next && !raw.includes(EMPTY_MATH_SLOT_FILL))) {
      element.setPromptValue?.(id, next || EMPTY_MATH_SLOT_FILL, SILENT);
    }
  }
}

export function sameSlotValues(a: SlotValues, b: SlotValues | undefined) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b ?? {})]);
  return [...keys].every(key => (a[key] ?? '') === (b?.[key] ?? ''));
}

/** Mark every slot correct / incorrect / ungraded; the look is ours (answerSlotStyles). */
export function applySlotStates(element: MathFieldElement, states: SlotStates | undefined) {
  for (const id of element.getPrompts?.() ?? []) {
    const state = states?.[id];
    element.setPromptState?.(id, state === true ? 'correct' : state === false ? 'incorrect' : 'undefined', false);
  }
}

/**
 * A click on the fixed part of an expression leaves the caret where nothing can be typed, and the student
 * keyboard's keys would be refused. Move it into the first empty slot, or else the end of the last one.
 */
export function moveCaretIntoSlot(element: MathFieldElement) {
  if (element.isSelectionEditable) return;
  const prompts = element.getPrompts?.() ?? [];
  const target = prompts.find(id => isEmptySlot(element, id)) ?? prompts[prompts.length - 1];
  const range = target ? element.getPromptRange?.(target) : null;
  if (range) element.position = range[1];
}

/**
 * Tab / Shift+Tab between slots, in reading order. Returns false at either end, so the key then moves
 * focus out of the field as usual.
 */
export function moveCaretToAdjacentSlot(element: MathFieldElement, direction: 1 | -1): boolean {
  const prompts = element.getPrompts?.() ?? [];
  const current = slotAtCaret(element);
  const index = current === undefined ? (direction === 1 ? -1 : prompts.length) : prompts.indexOf(current);
  const target = prompts[index + direction];
  const range = target ? element.getPromptRange?.(target) : null;
  if (!range) return false;
  element.position = range[1];
  return true;
}

/**
 * Each slot with the frame MathLive drew it in, in reading order — the order of the author's answer fields.
 * MathLive's DOM does NOT follow reading order (a fraction renders its denominator first), and a frame does
 * not carry its slot's id, so each slot is matched to the frame drawn where MathLive says that slot is. A
 * slot MathLive cannot place yet is left out.
 */
export function slotFrames(element: MathFieldElement): Array<{ id: string; index: number; frame: HTMLElement }> {
  const frames = Array.from(element.shadowRoot?.querySelectorAll<HTMLElement>('.ML__prompt') ?? []);
  const unclaimed = new Set(frames);
  const placed: Array<{ id: string; index: number; frame: HTMLElement }> = [];
  (element.getPrompts?.() ?? []).forEach((id, index) => {
    const range = element.getPromptRange?.(id);
    const bounds = range ? element.getElementInfo?.(range[0])?.bounds : undefined;
    if (!bounds) return;
    let closest: HTMLElement | undefined;
    let closestDistance = Infinity;
    for (const frame of unclaimed) {
      const rect = frame.getBoundingClientRect();
      const distance = Math.hypot(
        rect.x + rect.width / 2 - (bounds.x + bounds.width / 2),
        rect.y + rect.height / 2 - (bounds.y + bounds.height / 2)
      );
      if (distance < closestDistance) [closest, closestDistance] = [frame, distance];
    }
    if (!closest) return;
    unclaimed.delete(closest);
    placed.push({ id, index, frame: closest });
  });
  return placed;
}

/**
 * Number the slots the author sees, 1, 2 … in reading order, so each answer field can be matched to its
 * slot. The number goes on the slot's frame (`data-slot-number`, drawn by answerSlotStyles).
 */
export function numberSlots(element: MathFieldElement) {
  for (const { index, frame } of slotFrames(element)) {
    if (frame.dataset.slotNumber !== String(index + 1)) frame.dataset.slotNumber = String(index + 1);
  }
}

/**
 * Name each student slot for a page-scanning agent: `data-agent-step-id` on the slot's frame, holding what
 * `idFor` answers for the slot — one id per element, the way a plain blank's wrapper carries its own. The
 * frame is where the slot is drawn, inside the field's open shadow root, so a scanner finds the slot where
 * the student does. A slot `idFor` names nothing for (its question declares no steps) carries no attribute.
 */
export function tagSlots(element: MathFieldElement, idFor: (slotId: string) => string | undefined) {
  for (const { id, frame } of slotFrames(element)) {
    const stepId = idFor(id);
    if (stepId === undefined) delete frame.dataset.agentStepId;
    else if (frame.dataset.agentStepId !== stepId) frame.dataset.agentStepId = stepId;
  }
}

/**
 * Keep the slots labelled (`numberSlots`, `tagSlots`) while the field lives: MathLive rebuilds its DOM on
 * every change, which drops the labels, so `label` is re-applied after each rebuild. Returns the detach.
 */
export function attachSlotLabeling(element: MathFieldElement, label: (element: MathFieldElement) => void): () => void {
  const root = element.shadowRoot;
  if (!root) return () => undefined;
  let frame = 0;
  const relabel = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => label(element));
  };
  const observer = new MutationObserver(relabel);
  observer.observe(root, { childList: true, subtree: true });
  relabel();
  return () => {
    observer.disconnect();
    window.cancelAnimationFrame(frame);
  };
}

/** The slot the caret is in, if any. */
export function slotAtCaret(element: MathFieldElement): string | undefined {
  const position = element.position;
  if (position === undefined) return undefined;
  return (element.getPrompts?.() ?? []).find(id => {
    const range = element.getPromptRange?.(id);
    return range ? position >= range[0] && position <= range[1] : false;
  });
}

/**
 * Re-seed an author's template field without losing their place: the caret lands just after the slot
 * they were in, or after the slot an edit just created. One past a slot's content is still inside its
 * parent (the root, the numerator), which is where the author keeps typing.
 */
export function setTemplateKeepingCaret(element: MathFieldElement, latex: string) {
  const before = element.getPrompts?.() ?? [];
  const caretSlot = slotAtCaret(element);
  element.setValue?.(latex);
  const after = element.getPrompts?.() ?? [];
  const target =
    (caretSlot && after.includes(caretSlot) ? caretSlot : undefined) ?? after.find(id => !before.includes(id));
  const range = target ? element.getPromptRange?.(target) : null;
  if (range) element.position = range[1] + 1;
}
